/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

import { atom } from "jotai";
import { BaseService } from "@/frameworks/libraries/restservice";

/**
 * @deprecated use MyRoomServiceAPI instead
 */
class MyRoom extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (myRoomId: string, path?: string) => `/api/my_room/${myRoomId}${path ? `/${path}` : ""}`;

	private static Cache = {
		Inquiry: atom<WebServices.MyRoom.Inquiry.Response>({}),
		Participants: atom<WebServices.MyRoom.Participants.Response>({}),
		Details: atom<WebServices.MyRoom.Details.Response>({}),
		CheckBeforeParticipate: atom<WebServices.MyRoom.CheckBeforeParticipate.Response>({}),
		Participate: atom<WebServices.MyRoom.Participate.Response>({}),
		Leave: atom<WebServices.MyRoom.Leave.Response>({}),
		Update: atom<WebServices.MyRoom.Update.Response>({}),
		VisitorCount: atom<WebServices.MyRoom.VisitorCount.Response>({}),
		Authorities: atom<WebServices.MyRoom.Authority.Response>({})
	};

	public inquiry = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId }: WebServices.MyRoom.Inquiry.Request) => this.get(this.APIPath(myRoomId)),
		cache: MyRoom.Cache.Inquiry
	});

	public participants = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.MyRoom.Participants.Request) =>
			this.get(this.APIPath(parameter.myRoomId, "participants"), parameter),
		cache: MyRoom.Cache.Participants
	});

	public details = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId }: WebServices.MyRoom.Details.Request) => this.get(this.APIPath(myRoomId, "details")),
		cache: MyRoom.Cache.Details
	});
	public checkBeforeParticipate = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId }: WebServices.MyRoom.CheckBeforeParticipate.Request) =>
			this.get(this.APIPath(myRoomId, "participate")),
		cache: MyRoom.Cache.CheckBeforeParticipate
	});

	public participate = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId, ...parameter }: WebServices.MyRoom.Participate.Request) =>
			this.post(this.APIPath(myRoomId, "participate"), parameter),
		cache: MyRoom.Cache.Participate
	});

	public leave = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId, ...parameter }: WebServices.MyRoom.Leave.Request) =>
			this.post(this.APIPath(myRoomId, "leave"), parameter),
		cache: MyRoom.Cache.Leave
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId, ...rest }: WebServices.MyRoom.Update.Request) => this.put(this.APIPath(myRoomId), rest),
		cache: MyRoom.Cache.Update
	});

	public visitorCount = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId }: WebServices.MyRoom.VisitorCount.Request) =>
			this.get(this.APIPath(myRoomId, "visitor_count")),
		cache: MyRoom.Cache.VisitorCount
	});
	public authorities = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId }: WebServices.MyRoom.Authority.Request) =>
			this.get(this.APIPath(myRoomId, "authorities")),
		cache: MyRoom.Cache.Authorities
	});
}

class MyRoomGuestBook extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (myRoomId: string, guestBookId?: string, path?: string) =>
		`/api/my_room/${myRoomId}/guest_books${guestBookId ? `/${guestBookId}` : ""}${path ? `/${path}` : ""}`;
	private static Cache = {
		Delete: atom<WebServices.MyRoomGuestBook.Delete.Response>({}),
		Search: atom<WebServices.MyRoomGuestBook.Search.Response>({}),
		Write: atom<WebServices.MyRoomGuestBook.Write.Response>({}),
		Update: atom<WebServices.MyRoomGuestBook.Update.Response>({}),
		Hide: atom<WebServices.MyRoomGuestBook.Update.Response>({})
	};

	public deleteItem = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId, guestBookId }: WebServices.MyRoomGuestBook.Delete.Request) =>
			this.delete(this.APIPath(myRoomId, guestBookId)),
		cache: MyRoomGuestBook.Cache.Delete
	});

	public search = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.MyRoomGuestBook.Search.Request) =>
			this.get(this.APIPath(parameter.myRoomId), parameter),
		cache: MyRoomGuestBook.Cache.Search
	});

	public write = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId, ...rest }: WebServices.MyRoomGuestBook.Write.Request) =>
			this.post(this.APIPath(myRoomId), rest),
		cache: MyRoomGuestBook.Cache.Write
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId, guestBookId, ...rest }: WebServices.MyRoomGuestBook.Update.Request) =>
			this.put(this.APIPath(myRoomId, guestBookId), rest),
		cache: MyRoomGuestBook.Cache.Update
	});

	public hide = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId, guestBookId, ...rest }: WebServices.MyRoomGuestBook.Hide.Request) =>
			this.put(this.APIPath(myRoomId, guestBookId, "hide"), rest),
		cache: MyRoomGuestBook.Cache.Hide
	});
}

class MyRoomActivity extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (myRoomId: string) => `/api/my_room/${myRoomId}/activities`;

	private static Cache = {
		Activities: atom<WebServices.MyRoomActivity.Activities.Response>({})
	};

	public activities = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.MyRoomActivity.Activities.Request) =>
			this.get(this.APIPath(parameter.myRoomId), parameter),
		cache: MyRoomActivity.Cache.Activities
	});
}

class MyRoomNotification extends BaseService {
	constructor() {
		super();
	}
	private static Cache = {
		SpaceChatNotification: atom<WebServices.MyRoom.Notification.Response>({})
	};

	private APIPath = (myRoomId: string) => `/api/my_room/${myRoomId}/space_chat_notification`;

	public updateSpaceChatNotification = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ myRoomId, ...rest }: WebServices.MyRoom.Notification.Request) =>
			this.post(this.APIPath(myRoomId), rest),
		cache: MyRoomNotification.Cache.SpaceChatNotification
	});
}
class MyRoomGuestInvite extends BaseService {
	private APIPath = (spaceId: string, path?: string) => `/api/my_room/${spaceId}${path ? `/${path}` : ""}`;
	private static Cache = {
		Guest: {
			Renew: atom<WebServices.SpaceInvite.Guest.Renew.Response>({}),
			Inquiry: atom<WebServices.SpaceInvite.Guest.Inquiry.Response>({}),
			InquiryByKey: atom<WebServices.SpaceInvite.Guest.InquiryByKey.Response>({})
		}
	};

	public guest = {
		renew: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ spaceId, ...rest }: WebServices.SpaceInvite.Guest.Renew.Request) =>
				this.post(this.APIPath(spaceId, "guest_invite/renew"), rest),
			cache: MyRoomGuestInvite.Cache.Guest.Renew
		}),
		inquiry: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ spaceId, ...rest }: WebServices.SpaceInvite.Guest.Inquiry.Request) =>
				this.get(this.APIPath(spaceId, "guest_invite/code"), rest),
			cache: MyRoomGuestInvite.Cache.Guest.Inquiry
		}),
		inquiryByKey: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.SpaceInvite.Guest.InquiryByKey.Request) =>
				this.get(this.APIPath("guest_invite_code"), parameter),
			cache: MyRoomGuestInvite.Cache.Guest.InquiryByKey
		})
	};
}

/**
 * @deprecated use MyRoomServiceAPI instead
 */
class MyRoomServiceStatic {
	constructor(
		public readonly myRoom = new MyRoom(),
		public readonly myRoomGuestBook = new MyRoomGuestBook(),
		public readonly myRoomActivity = new MyRoomActivity(),
		public readonly myRoomNotification = new MyRoomNotification(),
		public readonly myRoomInvite = new MyRoomGuestInvite()
	) {}
}

const MyRoomService = new MyRoomServiceStatic();
export default MyRoomService;
